import { createSlice } from "@reduxjs/toolkit";

const initialState = {
	collections: {},
	tags: {},
	bundles: {},
	search: "",
	isSearchOpen: false,
	isFiltersOpen: false,
};

const filtersSlice = createSlice({
	name: "filters",
	initialState,
	reducers: {
		toggleFilterPanel(state) {
			state.isFiltersOpen = !state.isFiltersOpen;
		},
		toggleFilter(state, action) {
			const tag = action.payload;
			if (state.tags[tag]) {
				state.tags[tag].isActive = !state.tags[tag].isActive;
			}
		},
		toggleBundleFilter(state, action) {
			const bundle = action.payload;
			const existing = state.bundles[bundle.title];
			state.bundles[bundle.title] = {
				...existing,
				title: bundle.title,
				products: bundle.products,
				isActive: !existing?.isActive,
			};
		},
		setActiveFilters(state, action) {
			state.tags = action.payload.tags;
			state.bundles = action.payload.bundles;
		},
		setFilteredCollection(state, action) {
			const { handle, data } = action.payload;
			state.collections[handle] = data;
		},
		clearFilters(state) {
			state.tags = Object.fromEntries(
				Object.entries(state.tags).map(([tag, { displayName }]) => [
					tag,
					{ displayName, isActive: false, isSettled: false },
				])
			);
			state.bundles = Object.fromEntries(
				Object.entries(state.bundles).map(([title, bundle]) => [
					title,
					{ ...bundle, isActive: false, isSettled: false },
				])
			);
		},
		clearFilter(state, action) {
			const tag = action.payload;
			if (state.tags[tag]) {
				state.tags[tag].isActive = false;
				state.tags[tag].isSettled = false;
			}
		},
		clearBundleFilter(state, action) {
			const title = action.payload;
			if (state.bundles[title]) {
				state.bundles[title].isActive = false;
				state.bundles[title].isSettled = false;
			}
		},
		setSearch(state, action) {
			state.search = action.payload;
		},
		toggleSearch(state) {
			state.isSearchOpen = !state.isSearchOpen;
		},
		applyFilters(state) {
			for (const tag in state.tags) {
				state.tags[tag].isSettled = state.tags[tag].isActive;
			}
			for (const title in state.bundles) {
				state.bundles[title].isSettled = state.bundles[title].isActive;
			}
			state.isFiltersOpen = false;
		},
	},
});

export const {
	toggleFilterPanel,
	toggleFilter,
	toggleBundleFilter,
	setActiveFilters,
	setFilteredCollection,
	clearFilters,
	clearFilter,
	clearBundleFilter,
	setSearch,
	toggleSearch,
	applyFilters,
} = filtersSlice.actions;

import {
	filterTagsByAttribute,
	filterProducts,
	getAllProductTags,
	mapFilterBundles,
	mapFilterTags,
	filterBundlesByAttribute,
} from "utils/filters";
import getUrlParams from "helpers/get-url-params";


// Thunk for filtering collection products
export const filterCollectionProducts = ({ products, handle, title }) => (dispatch, getState) => {
	const { tags, bundles } = getState().filters;

	const settledTags = filterTagsByAttribute(tags, "isSettled");
	const activeTags = filterTagsByAttribute(tags, "isActive");

	const settledBundleProducts = filterBundlesByAttribute(bundles, "isSettled").flatMap(b => b.products);
	const activeBundleProducts = filterBundlesByAttribute(bundles, "isActive").flatMap(b => b.products);

	let mappedProducts = products?.map(product => ({
		...product,
		tags: product?.tags?.length ? product.tags.map(t => t?.toLowerCase()) : null,
	}));

	const activeProducts = filterProducts(mappedProducts, activeTags, activeBundleProducts);

	if (settledTags.length || settledBundleProducts.length) {
		mappedProducts = filterProducts(mappedProducts, settledTags, settledBundleProducts);
	}

	dispatch(
		setFilteredCollection({
			handle,
			data: {
				tags: getAllProductTags(activeProducts),
				products: activeProducts.map(p => p.id),
				title,
			},
		})
	);

	return mappedProducts;
};

// Thunk for searching products
export const searchProducts = (searchText, { products, title, handle, pureContent }) => dispatch => {
	const matchedProducts = products?.filter(prod =>
		searchText.trim().split(" ").some(txt =>
			prod.title?.toLowerCase()?.includes(txt) ||
			prod.tags?.map(tag => tag?.toLowerCase())?.some(t => [txt, searchText].includes(t)) ||
			title?.toLowerCase()?.includes(txt) ||
			pureContent?.toLowerCase()?.includes(txt) ||
			prod.shortDescription?.toLowerCase()?.includes(txt)
		)
	);

	dispatch(
		setFilteredCollection({
			handle,
			data: {
				tags: getAllProductTags(matchedProducts),
				products: matchedProducts.map(p => p.id),
				title,
			},
		})
	);

	return matchedProducts;
};

// Thunk for initializing filters based on URL params
export const initializeFilters = filters => dispatch => {
	const filterTags = mapFilterTags(filters);
	const filterBundles = mapFilterBundles(filters);

	const paramsConfig = [
		{ param: "tags", filters: filterTags },
		{ param: "bundles", filters: filterBundles },
	];

	paramsConfig.forEach(({ param, filters }) => {
		const paramValue = getUrlParams(param);
		if (paramValue) {
			paramValue.split(",").forEach(key => {
				if (Object.hasOwn(filters, key)) {
					filters[key].isActive = true;
					filters[key].isSettled = true;
				}
			});
		}
	});

	dispatch(setActiveFilters({ tags: filterTags, bundles: filterBundles }));
};


export default filtersSlice.reducer;
